import { createGlobalStyle, css } from 'styled-components';
import localFont from 'next/font/local';

export const ping = localFont({
	src: [
		{
			path: '../../public/fonts/PingLCG-Regular.woff2',
			weight: '400',
			display: 'swap',
			style: 'normal',
		},
		{
			path: '../../public/fonts/PingLCG-Medium.woff2',
			weight: '500',
			display: 'swap',
			style: 'normal',
		},
		{
			path: '../../public/fonts/PingLCG-Bold.woff2',
			weight: '600',
			display: 'swap',
			style: 'normal',
		},
		{
			path: '../../public/fonts/PingLCG-Heavy.woff2',
			weight: '700',
			display: 'swap',
			style: 'normal',
		},
	],
	preload: true,
	adjustFontFallback: true,
});

export const helvetica = localFont({
	src: [
		{
			path: '../../public/fonts/Helvetica-Regular.woff2',
			weight: '400',
			display: 'swap',
			style: 'normal',
		},
		{
			path: '../../public/fonts/Helvetica-Bold.woff2',
			weight: '600',
			display: 'swap',
			style: 'normal',
		},
	],
	preload: false,
	adjustFontFallback: true,
});

export const soundscape = localFont({
	src: [
		{
			path: '../../public/fonts/Soundscape-Bold.woff2',
			weight: '400',
			display: 'swap',
			style: 'normal',
		},
	],
	preload: false,
	adjustFontFallback: true,
});

function spaceGenerator() {
	let str = '';

	for (let i = 1; i <= 200; i++) {
		str += `--sp${i}x: calc(var(--spSize) * ${i});`;
	}

	return str;
}

const Variables = createGlobalStyle`${css`
	:root {
		//! Colors
		/*
        #15233B - ColorBigStone     #F59C00 - ColorApricot      #FFFFFF - ColorWhite

        #F8F8F8 - ColorPearl        #7B7F86 - ColorMediumGray   #FFE173 - ColorYellowTan

        #C8264C - ColorBrightMaroon #318D51 - ColorSeeGreen     #6E3C37 - ColorCowBoy

        #17428A - ColorCobalt       #5C293C - ColorWineBerry    #1E5F4B - ColorGreenPea

        #6EC8F0 - ColorMalibu       #0D1522 - ColorBlackPearl   #413278 - ColorPurpleHaze 

        #EAEAEA - ColorBrightGray   #FF6E73 - ColorBegonia
    */

		//* Primary Colors
		--primaryColor1: ${(props) => props.theme.colors.ColorBigStone};
		--primaryColor2: ${(props) => props.theme.colors.ColorApricot};
		--primaryColor3: ${(props) => props.theme.colors.ColorWhite};

		//* Secondary Colors
		--secondaryColor1: ${(props) => props.theme.colors.ColorPearl};
		--secondaryColor2: ${(props) => props.theme.colors.ColorMediumGray};
		--secondaryColor3: ${(props) => props.theme.colors.ColorYellowTan};
		--secondaryColor4: ${(props) => props.theme.colors.ColorBrightGray};
		--secondaryColor5: ${(props) => props.theme.colors.ColorBegonia};

		//* Backgrounds
		--bgBigStone: ${(props) => props.theme.colors.ColorBigStone};
		--bgApricot: ${(props) => props.theme.colors.ColorApricot};
		--bgCobalt: ${(props) => props.theme.colors.ColorCobalt};
		--bgYellowTan: ${(props) => props.theme.colors.ColorYellowTan};
		--bgBrightMaroon: ${(props) => props.theme.colors.ColorBrightMaroon};
		--bgGreenPea: ${(props) => props.theme.colors.ColorGreenPea};
		--bgWineBerry: ${(props) => props.theme.colors.ColorWineBerry};
		--bgMalibu: ${(props) => props.theme.colors.ColorMalibu};
		--bgBlackPearl: ${(props) => props.theme.colors.ColorBlackPearl};
		--bgPurpleHaze: ${(props) => props.theme.colors.ColorPurpleHaze};
		--bgCowBoy: ${(props) => props.theme.colors.ColorCowBoy};
		--bgBegonia: ${(props) => props.theme.colors.ColorBegonia};
		--bgPearl: ${(props) => props.theme.colors.ColorPearl};
		--bgLayer: ${(props) => props.theme.colors.ColorLayer};

		//* Message Color
		--successColor: ${(props) => props.theme.colors.ColorSeeGreen};
		--errorColor: ${(props) => props.theme.colors.ColorBrightMaroon};
		--shadowColor: #d6d6d6;

		//* Border Color
		--borderColor: ${(props) => `${props.theme.colors.ColorMediumGray}80`};

		//! Fonts
		--ping: ${ping.style.fontFamily};
		--helvetica: ${helvetica.style.fontFamily};
		--soundscape: ${soundscape.style.fontFamily};

		//! Screen Sizes
		--desktopSizeXL: ${(props) => props.theme.mediaQuery.DesktopSizeXL};
		--desktopSizeL: ${(props) => props.theme.mediaQuery.DesktopSizeL};
		--desktopSizeM: ${(props) => props.theme.mediaQuery.DesktopSizeM};
		--desktopSizeS: ${(props) => props.theme.mediaQuery.DesktopSizeS};
		--desktopSizeXS: ${(props) => props.theme.mediaQuery.DesktopSizeXS};
		--tabletSize: ${(props) => props.theme.mediaQuery.TabletSize};
		--tabletSizeS: ${(props) => props.theme.mediaQuery.TabletSizeS};
		--mobileSize: ${(props) => props.theme.mediaQuery.MobileSize};

		//! Spacing
		--spSize: 8px;
		--sp0-5x: calc(var(--spSize) * 0.5);
		--sp1-5x: calc(var(--spSize) * 1.5);
		--sp2-5x: calc(var(--spSize) * 2.5);
		--sp3-5x: calc(var(--spSize) * 3.5);
		${spaceGenerator()};

		//! Transition
		--trTime: 0.3s;

		//! Line heights
		--lineHeight: 1.6;
		--lineHeightS: 1.3;

		//! Burger Icon
		--burgerIconW: var(--sp3x);
		--burgerIconH: var(--sp3x);
	}

	@media only screen and (min-width: ${(props) => props.theme.mediaQuery.DesktopSizeXL}) {
		:root {
			--h1L: 184px;
			--h1: 152px;
			--h2: 80px;
			--h3: 56px;
			--h4: 48px;
			--h5: 40px;
			--h6: 32px;
			--p: 24px;
			--pS: 20px;
			--pXS: 16px;
			--pXXS: 14px;

			--headerFixHeight: 121px;
			--contPaddingH: var(--sp26x);
			--contMarginV: var(--sp25x);

			--colPadding: var(--sp3x);
		}
	}

	@media only screen and (max-width: ${(props) => props.theme.mediaQuery.DesktopSizeXLMin}) and (min-width: ${(props) => props.theme.mediaQuery.DesktopSizeL}) {
		:root {
			--h1L: 140px;
			--h1: 120px;
			--h2: 64px;
			--h3: 48px;
			--h4: 40px;
			--h5: 32px;
			--h6: 24px;
			--p: 18px;
			--pS: 16px;
			--pXS: 14px;
			--pXXS: 12px;

			--headerFixHeight: 100px;
			--contPaddingH: var(--sp20x);
			--contMarginV: var(--sp22x);

			--colPadding: var(--sp2x);
		}
	}

	@media only screen and (max-width: ${(props) => props.theme.mediaQuery.DesktopSizeLMin}) and (min-width: ${(props) => props.theme.mediaQuery.DesktopSizeM}) {
		:root {
			--h1L: 112px;
			--h1: 96px;
			--h2: 48px;
			--h3: 40px;
			--h4: 32px;
			--h5: 24px;
			--h6: 20px;
			--p: 18px;
			--pS: 14px;
			--pXS: 12px;
			--pXXS: 10px;

			--headerFixHeight: 82px;
			--contPaddingH: var(--sp15x);
			--contMarginV: var(--sp20x);

			--colPadding: var(--sp2x);
		}
	}

	@media only screen and (max-width: ${(props) => props.theme.mediaQuery.DesktopSizeMMin}) and (min-width: ${(props) => props.theme.mediaQuery.DesktopSizeS}) {
		:root {
			--h1L: 112px;
			--h1: 96px;
			--h2: 48px;
			--h3: 32px;
			--h4: 32px;
			--h5: 24px;
			--h6: 20px;
			--p: 18px;
			--pS: 14px;
			--pXS: 12px;
			--pXXS: 10px;

			--headerFixHeight: 82px;
			--contPaddingH: var(--sp15x);
			--contMarginV: var(--sp17x);

			--colPadding: var(--sp2x);
		}
	}

	@media only screen and (max-width: ${(props) => props.theme.mediaQuery.DesktopSizeSMin}) and (min-width: ${(props) => props.theme.mediaQuery.DesktopSizeXS}) {
		:root {
			--h1L: 88px;
			--h1: 80px;
			--h2: 40px;
			--h3: 32px;
			--h4: 32px;
			--h5: 20px;
			--h6: 20px;
			--p: 18px;
			--pS: 14px;
			--pXS: 12px;
			--pXXS: 10px;

			--headerFixHeight: 82px;
			--contPaddingH: var(--sp13x);
			--contMarginV: var(--sp15x);

			--colPadding: var(--sp2x);
		}
	}

	@media only screen and (max-width: ${(props) => props.theme.mediaQuery.DesktopSizeXSMin}) and (min-width: ${(props) => props.theme.mediaQuery.TabletSize}) {
		:root {
			--h1L: 72px;
			--h1: 56px;
			--h2: 32px;
			--h3: 32px;
			--h4: 24px;
			--h5: 20px;
			--h6: 20px;
			--p: 16px;
			--pS: 14px;
			--pXS: 12px;
			--pXXS: 10px;

			--headerFixHeight: 73px;
			--contPaddingH: var(--sp8x);
			--contMarginV: var(--sp13x);

			--colPadding: var(--sp1x);
		}
	}

	@media only screen and (max-width: ${(props) => props.theme.mediaQuery.TabletSizeMin}) and (min-width: ${(props) => props.theme.mediaQuery.TabletSizeS}) {
		:root {
			--h1L: 72px;
			--h1: 56px;
			--h2: 32px;
			--h3: 32px;
			--h4: 24px;
			--h5: 20px;
			--h6: 18px;
			--p: 16px;
			--pS: 14px;
			--pXS: 12px;
			--pXXS: 10px;

			--headerFixHeight: 73px;
			--contPaddingH: var(--sp8x);
			--contMarginV: var(--sp13x);

			--colPadding: var(--sp1x);
		}
	}

	@media only screen and (max-width: ${(props) => props.theme.mediaQuery.TabletSizeSMin}) {
		:root {
			--h1L: 56px;
			--h1: 32px;
			--h2: 32px;
			--h3: 24px;
			--h4: 20px;
			--h5: 20px;
			--h6: 18px;
			--p: 14px;
			--pS: 14px;
			--pXS: 12px;
			--pXXS: 10px;

			--headerFixHeight: 57px;
			--contPaddingH: var(--sp2x);
			--contMarginV: var(--sp12x);

			--colPadding: var(--sp1x);
		}
	}
`}`;

export default Variables;
